<template>
  <Fragment>
    <VAppBar
      app
      class="app-bar"
      clipped-left
      elevation="1"
    >
      <RouterLink
        :to="{ name: 'sessions' }"
        class="app-bar__logo"
        :title="vendor.title"
      >
        <img
          :src="vendor.branding?.logo ? `/${vendor.branding.logo}` : logotype"
          :alt="vendor.title"
        >
      </RouterLink>

      <VToolbarTitle class="text-md-center flex-grow-1">
        <RouterLink
          :to="{ name: 'sessions' }"
        >
          {{ vendor.title }}
        </RouterLink>
      </VToolbarTitle>

      <template v-if="!isGuest">
        <OrganizationSwitchMenu
          v-if="vendor.branding?.multiple_listener_accounts_enabled"
          :account="account"
        />

        <VBtn
          v-if="$vuetify.breakpoint.smAndUp && !isKaEvGroup"
          icon
          :title="$t('$app.calendar')"
          @click="goToEventCalendar"
        >
          <VIcon>event</VIcon>
        </VBtn>
      </template>

      <VBtn
        v-if="!vendor.branding?.hide_a11y_controls"
        class="bvi-open"
        icon
        :title="$t('$app.version_visually_impaired')"
        @click="bviRefreshPage"
      >
        <VIcon>$uchi_bvi</VIcon>
      </VBtn>

      <ContextHelp v-if="!isGuest" />

      <LocaleSwitch
        v-if="!vendor.branding?.hide_language_selector"
      />

      <VMenu
        v-if="!isGuest"
        offset-y
        open-on-hover
        left
      >
        <template #activator="{ on, attrs }">
          <VBtn
            v-bind="attrs"
            text
            :elevation="0"
            v-on="on"
          >
            <VAvatar
              v-if="account.profile.photo"
              size="32"
            >
              <VImg
                :src="`/${account.profile.photo}`"
                :alt="account.title"
              />
            </VAvatar>
            <VIcon v-else>
              person
            </VIcon>
            <span class="account-title">{{ accountTitle }}</span>
            <VIcon>expand_more</VIcon>
          </VBtn>
        </template>

        <VList dense>
          <template v-if="vendor.branding && !vendor.branding.old_listener_cabinet_hidden">
            <VListItem href="/training/sessions">
              <VListItemContent>
                <VListItemTitle>
                  Старый личный кабинет
                </VListItemTitle>
              </VListItemContent>
            </VListItem>
            <VDivider />
          </template>
          <VListItem :to="`/users/${account.id}`">
            <VListItemContent>
              <VListItemTitle>{{ $t('profiles.one') }}</VListItemTitle>
            </VListItemContent>
          </VListItem>
          <VListItem
            v-if="account.profile.is_default_listener && !vendor.branding?.hide_default_listener_orders_link"
            link
          >
            <VListItemContent>
              <VListItemTitle @click="dialog=true">
                {{ $t('$sdo.lc_menu_order_management.one') }}
              </VListItemTitle>
            </VListItemContent>
          </VListItem>
          <template v-if="menuItems && menuItems.length > 0">
            <VListItem
              v-for="menuItem in menuItems"
              :key="menuItem.name"
              :to="menuItem.to"
              :href="menuItem.href"
            >
              <VListItemContent>
                <VListItemTitle>{{ $t(menuItem.title) }}</VListItemTitle>
              </VListItemContent>
              <VListItemIcon>
                <VBadge
                  :value="menuItem.counter"
                  :content="menuItem.counter"
                  inline
                />
              </VListItemIcon>
            </VListItem>
          </template>
          <VDivider />
          <VListItem @click.prevent="logoutAccount">
            <VListItemContent>
              <VListItemTitle>{{ $t('anonymous_sign_out.one') }}</VListItemTitle>
            </VListItemContent>
          </VListItem>
        </VList>
      </VMenu>
    </VAppBar>

    <VDialog
      v-if="!isGuest"
      v-model="dialog"
      max-width="400"
    >
      <VCard>
        <VForm
          method="POST"
          :action="'/users/'+ account.parent_id + '/login'"
          autocomplete="off"
        >
          <VCardTitle>
            {{ $t('$app.go_management') }}
          </VCardTitle>
          <VDivider />
          <VCardActions>
            <VSpacer />
            <VBtn
              color="primary"
              text
              @click="dialog = false"
            >
              {{ $t('no.one') }}
            </VBtn>
            <VBtn
              color="error"
              type="submit"
            >
              {{ $t('yes.one') }}
            </VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>
  </Fragment>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Fragment } from 'vue-fragment'

import branchName from '@/mixins/branchName'

import { bviRefreshPage } from '@/utils/common'
import { GUEST, isGuest } from '@/utils/role'
import { THEMES } from '@/utils/themes'
import logotype from '@/assets/images/uchi.svg'
import ContextHelp from '@/components/ContextHelp.vue'
import LocaleSwitch from '@/components/LocaleSwitch.vue'
import OrganizationSwitchMenu from '@/components/OrganizationSwitchMenu.vue'

import 'bvi'

import * as getters from '@/store/getters/types'
import * as actions from '@/store/actions/types'

export default {
  name: 'ULayoutMain',
  components: {
    Fragment,
    ContextHelp,
    LocaleSwitch,
    OrganizationSwitchMenu,
  },

  mixins: [branchName],

  props: {
    menuItems: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      logotype,
      settings: false,
      dialog: false,
      theme: '',
      themes: Object.values(THEMES),
    }
  },

  computed: {
    ...mapGetters({
      account: getters.ACCOUNT,
      vendor: getters.VENDOR,
    }),

    accountTitle () {
      return this.account?.title
        .split(' ')
        .filter(str => str)
        .map((str, i) => i !== 0 ? `${str[0]}.` : str)
        .join(' ')
    },

    isGuest () {
      return isGuest(this.account?.role?.code || GUEST)
    }
  },

  mounted () {
    this.initBvi()
  },

  methods: {
    ...mapActions({
      logoutAccount: actions.LOGOUT,
    }),

    bviRefreshPage,

    initBvi () {
      if (window.isvek && window.isvek.Bvi) {
        this.createBviInstance()
      } else {
      // Если библиотека еще не загрузилась, ждем
        const checkInterval = setInterval(() => {
          if (window.isvek && window.isvek.Bvi) {
            clearInterval(checkInterval)
            this.createBviInstance()
          }
        }, 500)
      }
    },

    createBviInstance () {
      window.bvi = new isvek.Bvi({
        theme: 'white',
        images: 'grayscale',
        builtElements: true
      })
      bviRefreshPage()
    },

    goToEventCalendar () {
      if (this.$route.name !== 'eventCalendar') {
        this.$router.push({ name: 'eventCalendar' })
      }
    }
  }
}
</script>

<style lang="scss">
.v-application {
  .theme--light {
    &.app-bar.v-toolbar.v-sheet {
      background-color: white;
    }
  }

  .v-toolbar__title {
    font-size: map-deep-get($headings, "h4", "size");

    a,
    a:hover {
      color: inherit;
      text-decoration: none;
    }
  }

  .account-title {
    display: none;

    @media #{map-get($display-breakpoints, 'md-and-up')} {
      display: inline;
      text-transform: none;
      letter-spacing: normal;
      margin-right: $spacer;
      margin-left: $spacer;
    }
  }
}

.app-bar__logo {
   display: none;

  @media #{map-get($display-breakpoints, 'md-and-up')} {
    display: flex;
    align-items: center;
    height: var(--app-bar-height);
    padding: 0.5rem;
  }

  img {
    height: 100%;
    max-height: 100%;
    width: auto;
    object-fit: contain;
  }
}
</style>
